import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../../globalFunctions/DateTimeDisplayCleaner";
import AdminUserDrawer from "./AdminUserEdit";
import { useAuth } from "../../../contexts/AuthContext";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import DataGrid from "../../../components/Datagrid";
import Modal from "../../../components/controls/Modal";

export default function AdminUserTable({ fetchUsers, onEdit, onAdd }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const [availableRoles, setAvailableRoles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUsersAndRoles = async () => {
      setLoading(true);
      try {
        // Fetch users
        const userResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/User`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-User-Id": userId,
            },
          }
        );

        if (!userResponse.ok) throw new Error("Erreur chargement utilisateurs");
        const usersData = await userResponse.json();
        setUsers(usersData);

        // Fetch roles (admin-only)
        const rolesResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/User/Roles`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-User-Id": userId,
            },
          }
        );

        if (rolesResponse.ok) {
          const rolesData = await rolesResponse.json();
          setAvailableRoles(rolesData);
        } else {
          console.warn("Impossible de charger les rôles. Peut-être non-admin?");
        }
      } catch (err) {
        console.error(err);
        setError("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    };

    loadUsersAndRoles();
  }, [fetchUsers, userId]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsOpenModal(true);
    console.log("edituser", isOpenModal);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsOpenModal(true);
  };

  const handleSaveUser = (userData) => {
    setUsers((prev) => {
      const exists = prev.some((u) => u.id === userData.id);
      if (exists) {
        return prev.map((u) => (u.id === userData.id ? userData : u));
      } else {
        return [...prev, userData];
      }
    });

    setIsOpenModal(false);
    setSelectedUser(null);
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.fullName && u.fullName.toLowerCase().includes(search.toLowerCase()))
  );

  // Définition des colonnes pour DataGrid
  const columns = [
    {
      key: "email",
      label: t("adminUserTable.email"),
      width: "20%",
    },
    {
      key: "fullName",
      label: t("adminUserTable.fullName"),
      width: "20%",
      format: (value) => value || "-",
    },
    {
      key: "createdAt",
      label: t("adminUserTable.createdAt"),
      width: "15%",
      format: (value) => (value ? formatDateTime(value) : "-"),
    },
    {
      key: "lastSignInAt",
      label: t("adminUserTable.lastSignInAt"),
      width: "15%",
      format: (value) => (value ? formatDateTime(value) : "-"),
    },
    {
      key: "emailConfirmedAt",
      label: t("adminUserTable.emailConfirmedAt"),
      width: "15%",
      format: (value) => (value ? formatDateTime(value) : "-"),
    },
    {
      key: "actions",
      label: t("adminUserTable.actions"),
      width: "15%",
      render: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleEditUser(row);
          }}
          className="px-4 py-2 bg-primary text-white rounded-full hover:opacity-90 transition font-medium text-sm"
        >
          {t("adminUserTable.editButton")}
        </button>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6 text-primary">
      <button
        onClick={() => navigate(-1)}
        className="px-4 py-2 bg-primary text-white rounded-full flex items-center gap-2 hover:opacity-90 transition"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">{t("adminUserTable.title")}</h1>
        <div className="flex flex-col md:flex-row gap-2 items-center w-full md:w-auto">
          <input
            type="text"
            placeholder="Rechercher par email ou nom..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <button
            onClick={handleAddUser}
            className="px-4 py-2 bg-primary text-white rounded-full hover:opacity-90 transition font-medium whitespace-nowrap"
          >
            {t("adminUserTable.addButton")}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700">
          {t("adminUserTable.errorLoading")}
        </div>
      )}

      <DataGrid
        columns={columns}
        data={filteredUsers}
        loading={loading}
        emptyMessage={t("adminUserTable.noUsersFound")}
        className="shadow-lg"
      />

      <AdminUserDrawer
        key={selectedUser?.id || "new"}
        isOpen={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
        user={selectedUser}
        availableRoles={availableRoles}
        currentUserId={userId}
      />
    </div>
  );
}

// Demo Component
function Demo() {
  const mockUsers = [
    {
      id: 1,
      email: "jean.dupont@example.com",
      fullName: "Jean Dupont",
      createdAt: "2024-01-15T10:30:00Z",
      lastSignInAt: "2024-12-18T08:15:00Z",
      emailConfirmedAt: "2024-01-15T11:00:00Z",
    },
    {
      id: 2,
      email: "marie.martin@example.com",
      fullName: "Marie Martin",
      createdAt: "2024-02-20T14:20:00Z",
      lastSignInAt: "2024-12-17T16:45:00Z",
      emailConfirmedAt: "2024-02-20T15:00:00Z",
    },
    {
      id: 3,
      email: "pierre.bernard@example.com",
      fullName: "Pierre Bernard",
      createdAt: "2024-03-10T09:00:00Z",
      lastSignInAt: "2024-12-16T12:30:00Z",
      emailConfirmedAt: null,
    },
  ];

  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const columns = [
    {
      key: "email",
      label: "Email",
      width: "20%",
    },
    {
      key: "fullName",
      label: "Nom complet",
      width: "20%",
      format: (value) => value || "-",
    },
    {
      key: "createdAt",
      label: "Créé le",
      width: "15%",
      format: (value) =>
        value ? new Date(value).toLocaleDateString("fr-FR") : "-",
    },
    {
      key: "lastSignInAt",
      label: "Dernière connexion",
      width: "15%",
      format: (value) =>
        value ? new Date(value).toLocaleDateString("fr-FR") : "-",
    },
    {
      key: "emailConfirmedAt",
      label: "Email confirmé",
      width: "15%",
      format: (value) =>
        value ? new Date(value).toLocaleDateString("fr-FR") : "-",
    },
    {
      key: "actions",
      label: "Actions",
      width: "15%",
      render: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedUser(row);
            setIsModalOpen(true);
          }}
          className="px-4 py-2 bg-gray-900 text-white rounded-full hover:opacity-90 transition font-medium text-sm"
        >
          Modifier
        </button>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Gestion des utilisateurs
          </h1>
          <button className="px-4 py-2 bg-gray-900 text-white rounded-full hover:opacity-90 transition font-medium">
            Ajouter un utilisateur
          </button>
        </div>

        <DataGrid
          columns={columns}
          data={mockUsers}
          loading={false}
          emptyMessage="Aucun utilisateur trouvé"
          className="shadow-lg"
        />

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Modifier l'utilisateur"
          footer={modalFooter}
          size="md"
          showCloseButton
        >
          {selectedUser && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-semibold">Email:</span>{" "}
                {selectedUser.email}
              </div>
              <div>
                <span className="font-semibold">Nom:</span>{" "}
                {selectedUser.fullName || "-"}
              </div>
              <div>
                <span className="font-semibold">Créé le:</span>{" "}
                {new Date(selectedUser.createdAt).toLocaleDateString("fr-FR")}
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
